import { Dayjs } from "dayjs";
import apiClient from "../api/axios";
import { handleApiError } from "../api/handleApiError";

const BASE = "/v2/external/api-keys";

export type ExternalApiKey = {
  refId: string;
  serviceName: string;
  description: string;
  organization: number;
  organizationRef: string;
  status: "active" | "expired" | "terminated" | "delete"; // เผื่ออนาคต
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: { _id: string; displayName: string };
  lastUpdatedBy: { _id: string; displayName: string };
  apiKey?: string;
  lastUsed?: string;
};

export type ExternalApiService = {
  serviceRef: string;
  name: string;
};

export type CreateApiKeyPayload = {
  description?: string;
  serviceRef: string;
  expiresAt?: Dayjs;
};

const handleError = (error: unknown) =>
  handleApiError(error, {
    PLAN_EXPIRED: {
      messageKey: "PLAN_EXPIRED",
      titleKey: "Please-Upgrade-Package",
      type: "info",
    },
    VALIDATION_ERROR: "validation-failed",
    FORBIDDEN_ACCESS_ERROR: "Forbidden-Access-Error",
    RESOURCE_NOT_FOUND: "noResultsOverlayLabel",
    API_KEY_WITH_THIS_SERVICE_ALREADY_EXISTS:
      "API-Key-With-This-Service-Already-Exists",
  });

const externalApiKeyService = {
  getKeys: async (): Promise<ExternalApiKey[]> => {
    try {
      const res = await apiClient.get<{ results: ExternalApiKey[] }>(BASE);
      return res.data.results;
    } catch (error) {
      handleError(error);
    }
    return [];
  },

  createKey: async (
    payload: CreateApiKeyPayload,
  ): Promise<ExternalApiKey | undefined> => {
    try {
      const res = await apiClient.post<{ results: ExternalApiKey }>(
        BASE,
        payload,
      );
      return res.data.results;
    } catch (error) {
      handleError(error);
    }
  },

  revokeKey: async (id: string): Promise<boolean> => {
    try {
      await apiClient.delete(`${BASE}/${id}`);
      return true;
    } catch (error) {
      handleError(error);
    }
    return false;
  },

  regenerateKey: async (refId: string): Promise<ExternalApiKey | undefined> => {
    try {
      const res = await apiClient.post<{ results: ExternalApiKey }>(
        `${BASE}/regenerate/${refId}`,
      );
      return res.data.results;
    } catch (error) {
      handleError(error);
    }
  },

  updateDescription: async (
    refId: string,
    description: string,
  ): Promise<ExternalApiKey | undefined> => {
    try {
      const res = await apiClient.patch<{ results: ExternalApiKey }>(
        `${BASE}/${refId}`,
        { description },
      );
      return res.data.results;
    } catch (error) {
      handleError(error);
    }
  },

  getServices: async (): Promise<ExternalApiService[]> => {
    try {
      const res = await apiClient.get<{ results: ExternalApiService[] }>(
        `${BASE}/services`,
      );
      return res.data.results;
    } catch (error) {
      handleError(error);
    }
    return [];
  },
};

export default externalApiKeyService;
